import React, { useEffect, useState } from 'react';
import { Phone, Mail, Shield, Landmark, Bike, MapPin, Clock, Navigation, Camera, Instagram, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const benefits = [
    {
      icon: Shield,
      title: 'Sicherheit & Begleitung',
      description: 'Vergessen Sie Routenplanung und Pannen-Angst. Ihr erfahrener Guide Christian (Sportfreund Cowboy) kümmert sich um alles.'
    },
    {
      icon: Landmark,
      title: 'Kultur & Genuss',
      description: 'Wir halten an den versteckten Plätzen, die kein Reiseführer kennt, und erzählen die Geschichten der Region.'
    },
    {
      icon: Bike,
      title: 'Modernste Technik',
      description: 'Sie fahren auf den neuesten Premium-(E-)Bikes – für maximalen Komfort während der gesamten Tour.'
    }
  ];

  const tours = [
    {
      title: 'Die Seewinkel-Naturrunde',
      subtitle: 'Der Klassiker',
      duration: 'ca. 3h',
      distance: 'ca. 45 km',
      description: 'Wir rollen entspannt und ohne Hektik durch das UNESCO-Welterbe. Vorbei an den berühmten Salzlacken des Seewinkels, wo wir mit etwas Glück weiße Barockesel oder Wasserbüffel beim Grasen beobachten können. Die Tour führt uns direkt zum legendären Podersdorfer Leuchtturm – dem perfekten Fotostopp. Perfekt für Familien, Naturliebhaber und alle, die gemütlich in das Erlebnis starten wollen.',
      highlight: 'Ein Fernglas-Stopp an den Aussichtstürmen – perfekt für Naturliebhaber.',
      image: 'lighthouse',
      imageUrl: '/assets/leuchtturm.jpg'
    },
    {
      title: 'Die pannonische Kulturrunde',
      subtitle: 'Geschichte & Barock',
      duration: 'ca. 4–5h',
      distance: 'ca. 35 km',
      description: 'Diese Tour verbindet die weite Landschaft mit den kulturellen Schätzen der Region. Unser erstes großes Ziel ist die prächtige Basilika in Frauenkirchen. Von dort aus radeln wir weiter zum herrschaftlichen Schloss Halbturn, wo wir im kaiserlichen Schlosspark eine entspannte Pause einlegen. Ideal für Kulturinteressierte und E-Biker, die Genuss und Sehenswürdigkeiten verbinden möchten.',
      highlight: 'Ein Besuch in der aktuellen Ausstellung im Schloss Halbturn.',
      image: 'castle',
      imageUrl: '/assets/frauenkirchen.jpg'
    },
    {
      title: 'Die Bratislava-Expedition',
      subtitle: 'Grenzenloses Abenteuer',
      duration: 'ca. 6–7h',
      distance: 'ca. 80 km',
      description: 'Für alle, die etwas weiter hinaus wollen und die sportliche Abwechslung suchen. Wir nutzen die flachen, perfekt ausgebauten Radwege entlang der Parndorfer Platte und überqueren die Grenze bis direkt in die wunderschöne, historische Altstadt von Bratislava. Perfekt für sportliche Fahrer und aktive Gruppen, die Ausdauer mit einem coolen Städtetrip kombinieren wollen.',
      highlight: 'Gemeinsamer Kaffee und Kuchen in der Innenstadt.',
      image: 'bridge',
      imageUrl: '/assets/bratislava.jpg'
    }
  ];

  return (
    <div className="cowboy-website">
      {/* Hero Section */}
      <section 
        className="hero-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/hero.png)` }}
      >
        <div className="hero-overlay" />
        <div className="hero-content" style={{ transform: `translateY(${scrollY * 0.5}px)` }}>
          <h1 className="hero-title">COWBOY | DEIN SPORTFREUND</h1>
          <p className="hero-subtitle">Das Burgenland mit Gefühl erleben</p>
          <h2 className="hero-year">Rad Erlebnis Touren</h2>
          <a href="#touren">
            <Button className="hero-cta" size="lg">
              <Navigation className="mr-2" />
              Jetzt Touren entdecken
            </Button>
          </a>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="philosophy-section">
        <div className="container">
          <div className="philosophy-content">
            <p className="philosophy-text">
              „Wir lassen die Hektik hinter uns. Auf unseren geführten (E-)Bike-Touren erkunden Sie die schönsten Plätze der Region Neusiedler See in entspanntem Tempo. Ganz ohne Anstrengung, aber mit vollem Erlebnis."
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <div className="benefits-grid">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="benefit-card">
                  <CardContent className="benefit-content">
                    <div className="benefit-icon-wrapper">
                      <Icon className="benefit-icon" />
                    </div>
                    <h3 className="benefit-title">{benefit.title}</h3>
                    <p className="benefit-description">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* For All Ages Section */}
      <section className="all-ages-section">
        <div className="container">
          <div className="all-ages-content">
            <div className="all-ages-image">
              <img 
                src="/assets/all-ages.png" 
                alt="Ältere Gäste auf E-Bike Tour" 
              />
            </div>
            <div className="all-ages-text">
              <h2 className="all-ages-title">Für Alle Altersgruppen</h2>
              <p className="all-ages-description">
                Unsere E-Bike Touren sind speziell so konzipiert, dass sie für alle Altersgruppen geeignet sind. Dank der modernsten E-Bike-Technologie können auch unsere älteren Gäste die wunderschöne Landschaft des Burgenlandes genießen – ganz ohne Anstrengung, aber mit vollem Erlebnis.
              </p>
              <p className="all-ages-description">
                Ob 25 oder 75 Jahre – bei uns ist jeder willkommen. Die elektrische Unterstützung macht jede Tour zu einem entspannten Erlebnis, bei dem Sie die Natur, Kultur und Gastfreundschaft der Region in Ihrem eigenen Tempo erkunden können.
              </p>
              <div className="all-ages-highlights">
                <div className="all-ages-highlight-item">
                  <span className="highlight-check">✓</span>
                  <span>Keine besondere Fitness erforderlich</span>
                </div>
                <div className="all-ages-highlight-item">
                  <span className="highlight-check">✓</span>
                  <span>Regelmäßige Pausen & gemütliches Tempo</span>
                </div>
                <div className="all-ages-highlight-item">
                  <span className="highlight-check">✓</span>
                  <span>Premium E-Bikes für maximalen Komfort</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tours Section */}
      <section id="touren" className="tours-section">
        <div className="container">
          <h2 className="section-title">Unsere Erlebnis-Touren</h2>
          <div className="tours-grid">
            {tours.map((tour, index) => (
              <Card key={index} className="tour-card">
                {tour.imageUrl ? (
                  <div 
                    className="tour-image-real"
                    style={{ backgroundImage: `url(${tour.imageUrl})` }}
                  />
                ) : (
                  <div className="tour-image-placeholder">
                    <span className="tour-image-text">{tour.image}</span>
                  </div>
                )}
                <CardContent className="tour-content">
                  <div className="tour-header">
                    <h3 className="tour-title">{tour.title}</h3>
                    <p className="tour-subtitle">{tour.subtitle}</p>
                  </div>
                  <div className="tour-meta">
                    <div className="tour-meta-item">
                      <Clock className="tour-meta-icon" />
                      <span>{tour.duration}</span>
                    </div>
                    <div className="tour-meta-item">
                      <MapPin className="tour-meta-icon" />
                      <span>{tour.distance}</span>
                    </div>
                  </div>
                  <p className="tour-description">{tour.description}</p>
                  <div className="tour-highlight">
                    <strong>Highlight:</strong> {tour.highlight}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ÖBB Cherry Blossom Tour - Wide Card Style */}
      <section className="oebb-tour-section">
        <div className="container">
          <div className="oebb-tour-card">
            <div className="oebb-tour-image" data-testid="oebb-tour-image">
              <img 
                src="/assets/oebb-kirschblueten-banner.png" 
                alt="ÖBB Rail Tours - Kirschblütenradweg"
                className="oebb-tour-image-content"
                data-testid="oebb-banner-image"
              />
              <div className="oebb-image-copyright" data-testid="oebb-image-copyright">
                Copyright Sportfreund Cowboy <span className="copyright-note">(KI generiert)</span>
              </div>
            </div>
            <div className="oebb-tour-content">
              <div className="oebb-tour-header">
                <span className="oebb-partner-tag">In Kooperation mit ÖBB Rail Tours</span>
                <h3 className="oebb-tour-title">Mit dem Zug direkt ins Kirschblütenparadies und ins Freilichtmuseum Wander Bertoni</h3>
                <p className="oebb-tour-subtitle">Autofrei zum Radvergnügen</p>
              </div>

              <div className="oebb-tour-meta">
                <div className="oebb-tour-meta-item">
                  <Clock className="oebb-tour-meta-icon" />
                  <span>ca. 6h</span>
                </div>
                <div className="oebb-tour-meta-item">
                  <MapPin className="oebb-tour-meta-icon" />
                  <span>ca. 40 km</span>
                </div>
              </div>

              <p className="oebb-tour-description">
                Erleben Sie das Burgenland auf die entspannte Art: Kombinieren Sie eine komfortable Bahnfahrt mit einer geführten E-Bike-Genusstour rund um den Neusiedler See. Statt Stau, Parkplatzsuche und Stress genießen Sie eine klimafreundliche Anreise. Dank elektrischer Unterstützung radeln Sie entspannt entlang des beliebten Kirschblütenradwegs und durch die sanften Hügel des Leithagebirges. Vorbei an malerischen Weingärten, historischen Kulturlandschaften und traumhaften Aussichtspunkten eröffnet sich immer wieder der Blick auf den glitzernden Neusiedler See. Die rund 40 Kilometer lange E-Bike-Tour verbindet Natur, Kultur und Kulinarik zu einem einzigartigen Tageserlebnis. Buchen Sie online Ihr Erlebnisticket bestehend aus E-Bike-Genusstour + Bahnfahrt und profitieren Sie von einem ermäßigten Gesamtpreis!
              </p>

              <div className="oebb-tour-highlight">
                <strong>Highlight:</strong> Exklusive Führung im Freilichtmuseum Wander Bertoni.
              </div>

              <a 
                href="https://erlebnistickets.railtours.at/de/stories/erlebnisticket-e-bike-genusstour-am-kirschbluetenradweg-das-kombiticket-fuer-zug-und-pannonisches-genuss-abenteuer" 
                target="_blank" 
                rel="noopener noreferrer"
                className="oebb-booking-button"
              >
                Jetzt ÖBB Erlebnisticket buchen ➔
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Drone Footage Section */}
      <section className="drone-section">
        <div className="container">
          <div className="drone-content">
            <div className="drone-icon-wrapper">
              <Camera className="drone-main-icon" />
            </div>
            <h2 className="drone-title">Auf Wunsch Drohnenaufnahmen der Tour</h2>
            <p className="drone-subtitle">für WhatsApp oder Instagram</p>
            <p className="drone-description">
              Halten Sie Ihre Tour aus einer atemberaubenden Perspektive fest! 
              Auf Wunsch erstellen wir spektakuläre Drohnenaufnahmen Ihrer Radtour – 
              perfekt zum Teilen mit Freunden und Familie auf Social Media.
            </p>
            
            <div className="drone-video-wrapper">
              <video 
                className="drone-video"
                autoPlay
                muted
                loop
                playsInline
                controls
              >
                <source 
                  src="/assets/drone-video.mp4" 
                  type="video/mp4" 
                />
                Ihr Browser unterstützt das Video-Format nicht.
              </video>
              <p className="drone-video-caption">So könnte Ihre Tour aus der Vogelperspektive aussehen</p>
            </div>

            <div className="drone-features">
              <div className="drone-feature">
                <div className="drone-feature-icon">
                  <MessageCircle className="feature-icon" />
                </div>
                <span>WhatsApp</span>
              </div>
              <div className="drone-feature">
                <div className="drone-feature-icon">
                  <Instagram className="feature-icon" />
                </div>
                <span>Instagram</span>
              </div>
              <div className="drone-feature">
                <div className="drone-feature-icon">
                  <Camera className="feature-icon" />
                </div>
                <span>HD-Qualität</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <div className="pricing-content">
          <h2 className="pricing-title">Transparente Preise</h2>
          <div className="pricing-main">
            <p className="pricing-text">Touren bereits ab</p>
            <p className="pricing-amount">€ 49,-</p>
            <p className="pricing-unit">pro Person!</p>
          </div>
          <div className="pricing-includes">
            <p><strong>Inklusive Leistungen:</strong> Guide-Begleitung, Routenführung und wissenswerte Geschichten und Sagen über die Region, die in keinem Reiseführer zu lesen sind.</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <h2 className="section-title">Jetzt anmelden und mitfahren!</h2>
          <p className="contact-intro">
            Sichern Sie sich Ihren Platz auf einer unserer nächsten Touren direkt per Telefon oder E-Mail. Oder scannen Sie einfach den QR-Code auf unseren Flyern für die schnelle WhatsApp-Buchung direkt bei Christian.
          </p>
          <div className="contact-methods">
            <a href="tel:+436764237000" className="contact-item">
              <div className="contact-icon-wrapper">
                <Phone className="contact-icon" />
              </div>
              <div className="contact-details">
                <p className="contact-label">Telefon</p>
                <p className="contact-value">+43 676 423 7000</p>
              </div>
            </a>
            <a href="mailto:sportfreundcowboy@gmail.com" className="contact-item">
              <div className="contact-icon-wrapper">
                <Mail className="contact-icon" />
              </div>
              <div className="contact-details">
                <p className="contact-label">E-Mail</p>
                <p className="contact-value">sportfreundcowboy@gmail.com</p>
              </div>
            </a>
          </div>
          <div className="qr-code-placeholder">
            <div className="qr-code-box">
              <img 
                src="/assets/whatsapp-qr.jpg" 
                alt="WhatsApp QR-Code für Christian"
                className="qr-code-image"
              />
            </div>
            <p className="qr-code-text">Scannen für WhatsApp-Buchung</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-legal">
            <p>&copy; 2026 COWBOY | DEIN SPORTFREUND. Alle Rechte vorbehalten.</p>
            <div className="footer-links">
              <a href="/impressum">Impressum</a>
              <span className="footer-separator">|</span>
              <a href="/agb">AGB</a>
              <span className="footer-separator">|</span>
              <a href="/datenschutz">Datenschutz</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
